import React, { useState, useEffect } from 'react';

const ImpactMetrics: React.FC = () => {
  const [counters, setCounters] = useState({
    hoursSaved: 0,
    fasterHiring: 0,
    candidateQuality: 0,
    availability: 0
  });
  
  // Animated counter effect for Statistics Cards
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    
    const targets = {
      hoursSaved: 18,
      fasterHiring: 3,
      candidateQuality: 85,
      availability: 24
    };
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      setCounters({
        hoursSaved: Math.floor((targets.hoursSaved * step) / steps),
        fasterHiring: Math.floor((targets.fasterHiring * step * 10) / steps) / 10,
        candidateQuality: Math.floor((targets.candidateQuality * step) / steps),
        availability: Math.floor((targets.availability * step) / steps)
      });
      
      if (step >= steps) clearInterval(timer);
    }, interval);
    
    return () => clearInterval(timer);
  }, []);

  // Animated Statistics Cards
  const renderStatisticsCards = () => {
    const colorClasses = [
      { cardBg: 'bg-blue-900/30', cardBorder: 'border-blue-600/50', valueColor: 'text-blue-400' },
      { cardBg: 'bg-emerald-900/30', cardBorder: 'border-emerald-600/50', valueColor: 'text-emerald-400' },
      { cardBg: 'bg-purple-900/30', cardBorder: 'border-purple-600/50', valueColor: 'text-purple-400' },
      { cardBg: 'bg-cyan-900/30', cardBorder: 'border-cyan-600/50', valueColor: 'text-cyan-400' },
    ];
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            value: counters.hoursSaved,
            suffix: " hrs",
            label: "Saved Per Week",
            subtext: "Per recruiter on average",
            icon: "⏱️",
          },
          {
            value: counters.fasterHiring,
            suffix: "x",
            label: "Faster Hiring",
            subtext: "Reduced time-to-hire",
            icon: "⚡",
          },
          {
            value: counters.candidateQuality,
            suffix: "%",
            label: "Better Matches",
            subtext: "Higher quality candidates",
            icon: "🎯",
          },
          {
            value: counters.availability,
            suffix: "/7",
            label: "Always Available",
            subtext: "Automated outreach",
            icon: "🌐",
          }
        ].map((stat, index) => {
          const colors = colorClasses[index];
          return (
            <div 
              key={index}
              className={`p-6 text-center rounded-2xl border-2 ${colors.cardBg} ${colors.cardBorder} transition-all duration-200 ease-out hover:border-opacity-100`}
            >
              <div className="mb-4">
                <div className="text-3xl mb-3">{stat.icon}</div>
                <div className={`text-3xl font-bold ${colors.valueColor} mb-2`}>
                  {stat.value}{stat.suffix}
                </div>
                <div className="text-base font-semibold text-white mb-1">{stat.label}</div>
                <div className="text-xs text-gray-300">{stat.subtext}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <section className="pt-16 pb-24 md:pt-20 md:pb-32 bg-black relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="animate-fadeInUp">
            <p className="section-label">IMPACT</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The Impact on Your Team
            </h2>
          </div>
          <div className="animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            <p className="text-base text-gray-300 max-w-2xl mx-auto">
              Real numbers showing how Vertex Find transforms your hiring process, 
              saves time, and helps you find better candidates faster.
            </p>
          </div>
        </div>
        
        {/* Content Area - Statistics Cards */}
        <div className="animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
          {renderStatisticsCards()}
        </div>
      </div>
    </section>
  );
};

export default ImpactMetrics;
